export type UserRole = 'admin' | 'sales' | 'dev'
export type UserCompany = 'improtec' | 'feat'

export type AuthUser = {
  id: number
  email: string
  name: string
  role: UserRole
  company: UserCompany
  is_active: boolean
}

export type UserSummary = {
  id: number
  email: string
  name: string
  role: UserRole
  company: UserCompany
  is_active: boolean
}

export type CustomerStatus = 'active' | 'paused' | 'lost'

export type Customer = {
  id: number
  company_name: string
  billing_name: string | null
  industry: string | null
  tags: string | null
  sales_user_id: number
  dev_user_id: number
  status: CustomerStatus
  notes: string | null
  created_at: string
  updated_at: string
  sales_user_name?: string | null
  sales_user_company?: UserCompany | null
  dev_user_name?: string | null
  dev_user_company?: UserCompany | null
}

export type ServerKind = string // xserver / conoha / sakura / aws / other

export type Server = {
  id: number
  customer_id: number
  kind: ServerKind
  plan: string | null
  contract_expires_at: string | null
  admin_url: string | null
  ftp_info_encrypted: string | null
  db_info_encrypted: string | null
  ftp_info?: string | null
  db_info?: string | null
  wp_info: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

export type Domain = {
  id: number
  customer_id: number
  domain: string
  registrar: string | null
  registered_at: string | null
  expires_at: string | null
  auto_renew: number
  notes: string | null
  created_at: string
  updated_at: string
}

export type SslKind = 'lets_encrypt' | 'paid' | 'server_managed'

export type SslCertificate = {
  id: number
  domain_id: number
  kind: SslKind
  expires_at: string | null
  auto_renew: number
  notes: string | null
}

export type ContractKind = 'maintenance' | 'operation' | 'spot'

export type Contract = {
  id: number
  customer_id: number
  customer_name?: string | null
  kind: ContractKind
  monthly_amount: number
  started_at: string | null
  ended_at: string | null
  auto_renew: number
  notes: string | null
  created_at: string
  updated_at: string
}

export type ReportCategory =
  | 'routine'
  | 'incident'
  | 'change_request'
  | 'inquiry'

export type Report = {
  id: number
  customer_id: number
  server_id: number | null
  category: ReportCategory
  body: string
  work_minutes: number
  attachments_r2_paths: string | null // JSON 配列の文字列
  user_id: number
  occurred_at: string
  created_at: string
  customer_name?: string | null
  user_name?: string | null
}

export type UploadResult = {
  key: string
  size: number
  contentType: string
}

export type DashboardSummary = {
  expiring_30d: {
    domains: number
    ssl: number
    servers: number
    contracts: number
  }
  recurring_monthly_total: number
  recent_reports: Report[]
}

export type ExpiringDomain = {
  id: number
  domain: string
  expires_at: string
  customer_id: number
  customer_name: string
}

export type ExpiringSsl = {
  id: number
  kind: SslKind
  expires_at: string
  domain_id: number
  domain: string
  customer_id: number
  customer_name: string
}

export type ExpiringServer = {
  id: number
  kind: ServerKind
  expires_at: string
  customer_id: number
  customer_name: string
}

export type ExpiringContract = {
  id: number
  kind: ContractKind
  expires_at: string
  monthly_amount: number
  customer_id: number
  customer_name: string
}

export type ExpiringData = {
  window_days: number
  domains: ExpiringDomain[]
  ssl: ExpiringSsl[]
  servers: ExpiringServer[]
  contracts: ExpiringContract[]
}

export class ApiError extends Error {
  status: number
  code: string
  body: unknown
  constructor(status: number, message: string, code: string, body: unknown) {
    super(message)
    this.status = status
    this.code = code
    this.body = body
  }
}

type FetchOpts = Omit<RequestInit, 'body'> & {
  body?: unknown
  query?: Record<string, string | number | boolean | null | undefined>
  raw?: boolean
}

export async function api<T = unknown>(
  path: string,
  opts: FetchOpts = {},
): Promise<T> {
  const { body, query, headers, raw, ...rest } = opts
  let url = path
  if (query) {
    const sp = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue
      sp.set(k, String(v))
    }
    const qs = sp.toString()
    if (qs) url += `${path.includes('?') ? '&' : '?'}${qs}`
  }

  const isBinary = raw === true && body !== undefined
  const isFormData = body instanceof FormData

  const init: RequestInit = {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined && !isBinary && !isFormData
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(headers ?? {}),
    },
    ...rest,
  }
  if (body !== undefined) {
    if (isBinary) init.body = body as BodyInit
    else if (isFormData) init.body = body as FormData
    else init.body = JSON.stringify(body)
  }

  const res = await fetch(url, init)
  const text = await res.text()
  const parsed = text
    ? (() => {
        try {
          return JSON.parse(text)
        } catch {
          return text
        }
      })()
    : null
  if (!res.ok) {
    const code =
      (parsed && typeof parsed === 'object' &&
        (parsed as { error?: string }).error) ||
      `http_${res.status}`
    const message =
      (parsed && typeof parsed === 'object' &&
        ((parsed as { message?: string }).message ||
          (parsed as { error?: string }).error)) ||
      `通信エラー (${res.status})`
    throw new ApiError(res.status, message as string, code as string, parsed)
  }
  return parsed as T
}

export const STATUS_LABEL: Record<CustomerStatus, string> = {
  active: '稼働中',
  paused: '休止',
  lost: '失注',
}

export const CONTRACT_KIND_LABEL: Record<ContractKind, string> = {
  maintenance: '保守',
  operation: '運用',
  spot: 'スポット',
}

export const CATEGORY_LABEL: Record<ReportCategory, string> = {
  routine: '定常対応',
  incident: '障害対応',
  change_request: '変更依頼',
  inquiry: '問い合わせ',
}

export const ROLE_LABEL: Record<UserRole, string> = {
  admin: '管理者',
  sales: '営業',
  dev: '制作',
}

export const COMPANY_LABEL: Record<UserCompany, string> = {
  improtec: 'IMPROTEC',
  feat: 'FEAT.',
}

export const SSL_KIND_LABEL: Record<SslKind, string> = {
  lets_encrypt: "Let's Encrypt",
  paid: '有料証明書',
  server_managed: 'サーバー付帯',
}

export function formatDate(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatYen(n?: number | null): string {
  if (n === null || n === undefined) return '—'
  return `¥${n.toLocaleString('ja-JP')}`
}

export function daysUntil(value?: string | null): number | null {
  if (!value) return null
  const target = new Date(value).getTime()
  if (Number.isNaN(target)) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((target - today.getTime()) / (1000 * 60 * 60 * 24))
}

export function alertBadgeVariant(
  days: number | null,
): 'danger' | 'warning' | 'muted' | 'default' {
  if (days === null) return 'muted'
  if (days < 0) return 'danger'
  if (days <= 7) return 'danger'
  if (days <= 30) return 'warning'
  return 'default'
}

export function tagList(tags: string | null | undefined): string[] {
  if (!tags) return []
  return tags
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
}

export function parseAttachments(value: string | null | undefined): string[] {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) return parsed.filter((v) => typeof v === 'string')
    return []
  } catch {
    return []
  }
}

export function maskedSecret(value: string | null | undefined): boolean {
  return value === '••••••••'
}
